import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Grid, Container, Box } from '@mui/material';
import LoadingScreen from '../../loading/LoadingScreen';
import ImageSection from './components/ImageSection';
import ProductSection from './components/ProductSection';
import ProductDetails from './components/ProductDetails';
import MissingPage from '../../errorMessages/MissingPage';
import { apiFilterProducts } from '../../../../api/apiMultiFilter';
import { captureAnalytics } from '../../../../utilities/Analytics';

interface StockMaster {
  diameter?: string;
  width?: string;
  length_cm?: string;
  colour?: string;
  description_1?: string;
  design?: string;
  range?: string;
  brand?: string;
  composition?: string;
  group_desc?: string;
  on_hand?: number;
  cut_price_ex_vat?: string;
  price_retail_inc_vat?: string;
  item_group?: string;
  stock_link?: number;
  cleaning?: string;
  c_weight?: string;
  repeat_t?: string;
  repeat_h?: string;
  hanging_instructions?: string;
  railroaded?: string;
  application?: string;
  martindale?: string;
  po_delivery_date?: string;
  [key: string]: any;
}

interface Product {
  id?: string;
  sku?: string;
  primaryImageUrl?: string;
  additionalImages?: any[];
  long_description?: string;
  cleaning_instructions?: any[];
  standards?: any[];
  disambiguating_description?: string;
  [key: string]: any;
}

interface ProductItem {
  Product?: Product;
  StockMaster?: StockMaster;
  sku?: string;
  diameter?: string;
  width?: string;
  length_cm?: string;
  colour?: string;
  description_1?: string;
  design?: string;
  range?: string;
  brand?: string;
  composition?: string;
  group_desc?: string;
  on_hand?: number;
  cut_price_ex_vat?: string;
  price_retail_inc_vat?: string;
  item_group?: string;
  stock_link?: number;
  primaryImageUrl?: string;
  additionalImages?: any[];
  long_description?: string;
  cleaning_instructions?: any[];
  standards?: any[];
  disambiguating_description?: string;
  [key: string]: any;
}

function ProductPage() {
  // URL parameters
  const { item_group, collection, design, colour } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetSize = searchParams.get('targetSize');
  const targetProduct = searchParams.get('targetProduct');

  // 🔥 ADDED: Missing state variables from trade repo
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<ProductItem>();
  const [selectedImagePreviewIndex, setSelectedImagePreviewIndex] = useState<number>(0);
  const [width, setWidth] = useState('');
  const [lengthcm, setLengthCM] = useState('');
  const [diameter, setDiameter] = useState('');
  const [activeImageStep, setActiveImageStep] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const queryClient = useQueryClient();

  // Build query key
  const queryKey = useMemo(() => {
    const baseKey = [
      'product-filter',
      item_group,
      design,
      targetProduct
    ];

    if (targetProduct && targetSize) {
      if (diameter) {
        return [...baseKey, 'diameter', diameter];
      } else if (width && lengthcm) {
        return [...baseKey, 'dimensions', width, lengthcm];
      }
    }

    return baseKey;
  }, [item_group, design, colour, targetProduct, width, lengthcm, diameter, targetSize]);

  // Main query
  const {
    isSuccess,
    isError,
    data: productData,
    isLoading,
  } = useQuery(
    queryKey,
    async () => {
      console.log('Consumer ProductPage query running');

      // Use multifilter API to get ALL products for this design
      const multifilterProducts = await apiFilterProducts({
        filters: {
          item_group: item_group,
          design: design,
          range: collection, // Add collection filter
        },
        filter_fields: ["item_group", "design", "range", "colour"],
        main_filter_field: null,
        selection_fields: null,
        sort_by_setting: "Alphabetical A-Z",
        off_setter: { start: 0, end: 1000 },
        only_published: true,
        only_promo: false,
        only_in_stock: false,
        limited: false
      });

      console.log('🔥 Consumer API Request Filters:', {
        item_group: item_group,
        design: design,
        range: collection
      });

      console.log('Raw multifilter response:', multifilterProducts);

      // Extract data from API response
      const getDataItems = (response: any) => {
        if (!response) return [];

        if (response.success && response.products && Array.isArray(response.products)) {
          return response.products;
        }

        if (response.data && Array.isArray(response.data)) {
          return response.data;
        }

        if (Array.isArray(response)) {
          return response.slice(0, -1);
        }

        return [];
      };

      const productItems = getDataItems(multifilterProducts);
      console.log('All products for design:', productItems);

      return {
        data: productItems,
        selection_fields: {}
      };
    },
    {
      enabled: !!item_group && !!collection && !!design,
      staleTime: 60000,
      cacheTime: 60000,
      onSuccess: (data) => {
        console.log('Query onSuccess called with:', data);

        const items = data?.data || [];
        console.log('Items extracted:', items);

        if (!Array.isArray(items) || items.length === 0) {
          console.log('No items found or items is not an array');
          return;
        }

        // Handle initial load with target product
        if (isInitialLoad && targetProduct && targetSize) {
          console.log('Handling initial load with target:', { targetProduct, targetSize });

          const targetIndex = items.findIndex((item: ProductItem) => {
            const itemWidth = (item.StockMaster?.width || item.width || '').trim();
            const itemLength = (item.StockMaster?.length_cm || item.length_cm || '').trim();
            const itemDiameter = (item.StockMaster?.diameter || item.diameter || '').trim();
            const itemSku = item.Product?.sku || item.sku;

            const currentSize = itemDiameter && itemDiameter !== '0cm'
              ? itemDiameter
              : `${itemWidth}x${itemLength}`;

            const sizeMatch = currentSize === targetSize;
            const skuMatch = itemSku === targetProduct;

            console.log('Checking item:', { itemSku, currentSize, sizeMatch, skuMatch });

            return skuMatch && sizeMatch;
          });

          console.log('Target index found:', targetIndex);

          if (targetIndex !== -1) {
            const item = items[targetIndex];
            if (!item) return;

            Promise.resolve().then(() => {
              setCurrentIndex(targetIndex);
              setSelectedImagePreviewIndex(targetIndex);
              setActiveImageStep(0);

              const itemDiameter = item.StockMaster?.diameter || item.diameter;
              const itemWidth = item.StockMaster?.width || item.width;
              const itemLength = item.StockMaster?.length_cm || item.length_cm;

              if (itemDiameter && itemDiameter !== '0cm') {
                setDiameter(itemDiameter.trim());
                setWidth('');
                setLengthCM('');
              } else {
                const width = itemWidth?.replace('cm', '').trim() || '';
                const length = itemLength?.replace('cm', '').trim() || '';
                setWidth(width);
                setLengthCM(length);
                setDiameter('');
              }

              setTimeout(() => {
                setIsInitialLoad(false);
              }, 100);
            });
          } else {
            setIsInitialLoad(false);
          }
        } else if (isInitialLoad) {
          setIsInitialLoad(false);
        }

        // Set initial color if provided in URL
        if (colour !== undefined) {
          const initialColourIndex = items.findIndex(
            (item: any) => (item?.StockMaster?.colour || item?.colour) === colour
          );
          if (initialColourIndex !== -1) {
            setCurrentIndex(initialColourIndex);
            setSelectedImagePreviewIndex(initialColourIndex);
          }
        }
      }
    }
  );

  // Cache invalidation effect
  useEffect(() => {
    if (sessionStorage.getItem('fromFilterComponent') === 'true') {
      console.log('Invalidating cache due to filter component navigation');
      queryClient.invalidateQueries(['product-filter']);
      sessionStorage.removeItem('fromFilterComponent');
    }
  }, [location.pathname, queryClient]);

  useEffect(() => {
    if (location.pathname.includes('/design/')) {
      sessionStorage.setItem('fromFilterComponent', 'true');
    }

    return () => {
      if (window.history.state?.idx !== undefined &&
        window.history.state.idx > 0 &&
        location.pathname.includes('/design/')) {
        // Going back - keep flags intact
      } else {
        // Navigating forward - clean up
        sessionStorage.removeItem('fromFilterComponent');
        sessionStorage.removeItem('multifilter_active');
        sessionStorage.removeItem('multifilter_scroll_position');
        sessionStorage.removeItem('multifilter_item_group');
      }
    };
  }, [location.pathname]);

  const productDataItems = productData?.data || [];

  // Helper function to get property from either nested or flat structure
  const getProperty = (item: ProductItem, prop: string): any => {
    if (item && item[prop] !== undefined) {
      return item[prop];
    }

    if (item?.StockMaster && item.StockMaster[prop] !== undefined) {
      return item.StockMaster[prop];
    }

    if (item?.Product && item.Product[prop] !== undefined) {
      return item.Product[prop];
    }

    return undefined;
  };

  // Build product details list
  const productDetailsList =
    productDataItems && productDataItems.length > 0
      ? [
        {
          key: 'Product Code (SKU)',
          value: getProperty(productDataItems[currentIndex], 'sku'),
        },
        {
          key: 'Description',
          value: getProperty(productDataItems[currentIndex], 'description_1'),
        },
        {
          key: 'Design',
          value: getProperty(productDataItems[currentIndex], 'design'),
        },
        {
          key: 'Colour',
          value: getProperty(productDataItems[currentIndex], 'colour'),
        },
        {
          key: 'Range',
          value: getProperty(productDataItems[currentIndex], 'range'),
        },
        {
          key: 'Brand',
          value: getProperty(productDataItems[currentIndex], 'brand'),
        },
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Rugs'
          ? [
            {
              key: 'Size',
              value: `${getProperty(productDataItems[currentIndex], 'width')} x ${getProperty(productDataItems[currentIndex], 'length_cm')}`,
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'brand') === 'HAUS'
          ? [
            {
              key: 'Weight',
              value: getProperty(productDataItems[currentIndex], 'c_weight'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Width',
              value: getProperty(productDataItems[currentIndex], 'width'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Roll Dimension',
              value: `${getProperty(productDataItems[currentIndex], 'width')} x ${getProperty(productDataItems[currentIndex], 'length_cm')}`,
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric' ||
        getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Vertical Repeat',
              value: getProperty(productDataItems[currentIndex], 'repeat_t'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper' ||
        getProperty(productDataItems[currentIndex], 'group_desc') === 'Rugs'
          ? [
            {
              key: 'Composition',
              value: getProperty(productDataItems[currentIndex], 'composition'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Installation',
              value: getProperty(productDataItems[currentIndex], 'hanging_instructions'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Horizontal Repeat',
              value: getProperty(productDataItems[currentIndex], 'repeat_h'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Railroaded',
              value: getProperty(productDataItems[currentIndex], 'railroaded'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Composition',
              value: getProperty(productDataItems[currentIndex], 'composition'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Weight',
              value: getProperty(productDataItems[currentIndex], 'c_weight'),
            },
          ]
          : []),
        {
          key: 'Care Info',
          value: Array.isArray(getProperty(productDataItems[currentIndex], 'cleaning_instructions')) ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '7px',
                  marginBottom: '10px'
                }}
              >
                {getProperty(productDataItems[currentIndex], 'cleaning_instructions').map(
                  (instruction: { image_data?: any; file_name?: string }, index: number) =>
                    instruction.image_data ? (
                      <img
                        key={index}
                        src={instruction.image_data}
                        alt={instruction.file_name}
                        style={{
                          width: '30px',
                          height: '30px',
                          paddingRight: '0',
                        }}
                      />
                    ) : null
                )}
              </Box>
              <Box>
                {getProperty(productDataItems[currentIndex], 'cleaning_instructions')
                  .filter((instruction: any) => !instruction.image_data)
                  .map((instruction: any, index: number) => (
                    <span key={index}>
                      {instruction.cleaning_instruction_name || instruction.cleaning_instructions_name}
                      {index < getProperty(productDataItems[currentIndex], 'cleaning_instructions').filter((i: any) => !i.image_data).length - 1 ? '; ' : ''}
                    </span>
                  ))}
              </Box>
            </Box>
          ) : null,
        },
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Martindale Rubs',
              value: getProperty(productDataItems[currentIndex], 'martindale'),
            },
          ]
          : []),
        {
          key: 'Application',
          value: getProperty(productDataItems[currentIndex], 'application'),
        },
        {
          key: 'Comments',
          value: getProperty(productDataItems[currentIndex], 'disambiguating_description') ? (
            <Box>
              {getProperty(productDataItems[currentIndex], 'disambiguating_description')
                .split('\n')
                .filter((line: string) => line.trim() !== '')
                .map((line: string, index: number) => (
                  <span key={index}>
                    {line}
                    {index < getProperty(productDataItems[currentIndex], 'disambiguating_description').split('\n').filter((l: string) => l.trim() !== '').length - 1 && <br />}
                  </span>
                ))}
            </Box>
          ) : 'N/A',
        },
        {
          key: 'Standards',
          value: Array.isArray(getProperty(productDataItems[currentIndex], 'standards')) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
              }}
            >
              {getProperty(productDataItems[currentIndex], 'standards').map(
                (standard: any, index: any) => (
                  <img
                    key={index}
                    src={standard.image_data}
                    alt={standard.file_name}
                    style={{
                      height: '60px',
                      paddingRight: '0',
                    }}
                  />
                )
              )}
            </Box>
          ) : null,
        },
      ]
      : [];

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [colour]);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, `product-${design}`);
  }, [location.pathname, design]);

  // Loading and error states
  if (isLoading) return <LoadingScreen />;
  if (isError) return <MissingPage />;

  return (
    <>
      {isSuccess && productDataItems && (
        <>
          <Container maxWidth="xl">
            <Grid
              mt={{ xs: 5, sm: 10 }}
              container
              sx={{
                flexDirection: {
                  xs: 'column',
                  lg: 'row',
                },
              }}
            >
              <ImageSection
                productListData={productDataItems}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                currentSelectedItem={currentSelectedItem}
                selectedImagePreviewIndex={selectedImagePreviewIndex}
                activeImageStep={activeImageStep}
                setActiveImageStep={setActiveImageStep}
              />

              <ProductSection
                productListData={productDataItems} // 🔥 FIXED: Changed from productData to productListData
                productData={productData}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                itemGroup={item_group}
                application={getProperty(productDataItems[currentIndex], 'application')} // 🔥 FIXED: Get from productDataItems instead
                collection={collection}
                design={design}
                colour={colour}
                currentSelectedItem={currentSelectedItem}
                setCurrentSelectedItem={setCurrentSelectedItem}
                setSelectedImagePreviewIndex={setSelectedImagePreviewIndex}
                selectedImagePreviewIndex={selectedImagePreviewIndex}
                // 🔥 ADDED: Missing props
                setWidth={setWidth}
                setLengthCM={setLengthCM}
                setDiameter={setDiameter}
                diameter={diameter}
                width={width}
                lengthCM={lengthcm}
                activeImageStep={activeImageStep}
                setActiveImageStep={setActiveImageStep}
                isInitialLoad={isInitialLoad}
                setIsInitialLoad={setIsInitialLoad}
                userHasInteracted={userHasInteracted}
                setUserHasInteracted={setUserHasInteracted}
              />
            </Grid>

            <ProductDetails productDetailsList={productDetailsList} />
          </Container>
        </>
      )}
    </>
  );
}

export default ProductPage;